import React, { useEffect } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  selectCurrentOrganizationId,
  selectOrganizationUsersData,
} from '../../store/organization/organization-slice-selectors';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { resetUsersRestState } from '../../store/organization/organization-slice';
import useServerValidationResponseEffect from '@common/hooks/useServerValidationResponseEffect';
import useValidatedInputEffect from '@common/hooks/useValidatedInputEffect';
import TextInput from '@common/components/Inputs/TextInput';
import { isHttpError } from '@common/interfaces/api/http-error';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import {
  validateUserEmail,
  validateUserFirstName,
  validateUserLastName,
  validateUserPhoneNumber,
} from '../../services/validation/user-validation';
import SelectInput from '../forms/SelectInput';
import useValidatedSelectEffect from '../../hooks/useValidatedSelectEffect';
import UserRole from '../../models/enums/user-roles';
import { InfoIcon } from '@common/utils/icons/icons';
import { createUser, patchUserById } from '../../store/organization/organization-actions';
import User from '../../models/general/user';
import UserPatchType from '../../models/enums/user-patch-type';
import {
  selectInviteData,
  selectIsUserAdminOrHigher,
} from '../../store/general/general-slice-selectors';
import FormUser from '../../models/general/form-user';
import { Language } from '../../models/enums/languages';
import { registerUser } from '../../store/general/general-actions';
import { resetRegisterRestState } from '../../store/general/general-slice';
import Filters from '@common/enums/query-params/filters';

const UserForm: React.FC<{
  user?: User;
  open: boolean;
  onClose: () => void;
  memberRegistration?: {
    formDisabled: boolean;
    alert: React.ReactNode;
    organizationId: string | null;
    filters: Filters;
  };
}> = ({ user, open, onClose, memberRegistration }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const usersData = useAppSelector((state) => selectOrganizationUsersData(state));
  const currentOrganizationId = useAppSelector((state) => selectCurrentOrganizationId(state));
  const isAdmin = useAppSelector((state) => selectIsUserAdminOrHigher(state));
  const inviteData = useAppSelector((state) => selectInviteData(state));

  const { resetToggle, updateResetToggle, removeServerError, getServerValidationResultOrNull } =
    useServerValidationResponseEffect({
      postError: memberRegistration ? inviteData.rest.postError : usersData.rest.postError,
      resetPostHandler: memberRegistration
        ? () => dispatch(resetRegisterRestState())
        : () =>
            dispatch(resetUsersRestState({ postStatus: HttpFetchStatus.Idle, postError: null })),
      patchError: usersData.rest.patchError,
      resetPatchHandler: () =>
        dispatch(resetUsersRestState({ patchStatus: HttpFetchStatus.Idle, patchError: null })),
    });

  useEffect(() => {
    if (
      ((memberRegistration != null && inviteData.rest.postStatus === HttpFetchStatus.Success) ||
        usersData.rest.postStatus === HttpFetchStatus.Success ||
        usersData.rest.patchStatus === HttpFetchStatus.Success) &&
      open
    ) {
      updateResetToggle();
      onClose();
    }
  }, [
    inviteData.rest.postStatus,
    memberRegistration,
    onClose,
    open,
    updateResetToggle,
    usersData.rest.patchStatus,
    usersData.rest.postStatus,
  ]);

  const existingUserFirstName =
    user?.name != null && user.name.split(' ').length > 1 ? user.name.split(' ')[0] : null;
  const existingUserLastName =
    user?.name != null
      ? user.name.split(' ').splice(1, user.name.split(' ').length).join(' ')
      : null;

  const {
    value: firstName,
    validationResult: firstNameValidationResult,
    valueChangeHandler: firstNameChangeHandler,
  } = useValidatedInputEffect(
    existingUserFirstName ?? '',
    (newValue) => validateUserFirstName(newValue, memberRegistration != null),
    (_) => () => {},
    getServerValidationResultOrNull('firstName'),
    removeServerError,
    resetToggle,
  );

  const {
    value: lastName,
    validationResult: lastNameValidationResult,
    valueChangeHandler: lastNameChangeHandler,
  } = useValidatedInputEffect(
    existingUserLastName ?? '',
    (newValue) => validateUserLastName(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('lastName'),
    removeServerError,
    resetToggle,
  );

  const {
    value: email,
    validationResult: emailValidationResult,
    valueChangeHandler: emailChangeHandler,
  } = useValidatedInputEffect(
    user?.email ?? '',
    (newValue) => validateUserEmail(newValue, true, memberRegistration != null),
    (_) => () => {},
    getServerValidationResultOrNull('email'),
    removeServerError,
    resetToggle,
  );

  const {
    value: phoneNumber,
    validationResult: phoneNumberValidationResult,
    valueChangeHandler: phoneNumberChangeHandler,
  } = useValidatedInputEffect(
    user?.phoneNumber ?? '',
    (newValue) => validateUserPhoneNumber(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('phoneNumber'),
    removeServerError,
    resetToggle,
  );

  const {
    value: role,
    validationResult: roleValidationResult,
    valueChangeHandler: roleChangeHandler,
  } = useValidatedSelectEffect(
    user?.role ?? UserRole.MEMBER,
    (newValue) => ({ isValid: true, error: t('form-help-text-user-role') }),
    (_) => () => {},
    getServerValidationResultOrNull('role'),
    removeServerError,
    resetToggle,
  );

  const {
    value: language,
    validationResult: languageValidationResult,
    valueChangeHandler: languageChangeHandler,
  } = useValidatedSelectEffect(
    user?.language ?? Language.ENGLISH,
    (newValue) => ({ isValid: true, error: null }),
    (_) => () => {},
    getServerValidationResultOrNull('language'),
    removeServerError,
    resetToggle,
  );

  const addUserClickHandler = (event: React.FormEvent) => {
    event.preventDefault();
    if (memberRegistration != null) {
      if (memberRegistration.organizationId != null) {
        dispatch(
          registerUser({
            data: {
              firstName,
              lastName,
              email,
              phoneNumber,
              language: language as Language,
              organizationId: memberRegistration.organizationId,
              role: UserRole.MEMBER,
            },
            filters: memberRegistration.filters,
          }),
        );
      }
      return;
    }

    if (user?.id == null) {
      dispatch(
        createUser({
          firstName,
          lastName,
          email,
          phoneNumber,
          language: language as Language,
          organizationId: currentOrganizationId,
          role: role as UserRole,
        }),
      );
    } else {
      const patchData: Partial<FormUser> = {
        firstName,
        lastName,
        email,
        phoneNumber,
        language: language as Language,
      };
      if (user.role !== UserRole.OWNER && isAdmin) {
        patchData.role = role as UserRole;
      }
      dispatch(
        patchUserById({ id: user.id, patchType: UserPatchType.GeneralPatch, data: patchData }),
      );
    }
  };

  const closeHandler = () => {
    dispatch(
      resetUsersRestState({
        postStatus: HttpFetchStatus.Idle,
        postError: null,
        patchStatus: HttpFetchStatus.Idle,
        patchError: null,
        patchId: null,
        patchType: null,
      }),
    );
    updateResetToggle();
    onClose();
  };

  const availableRoles: UserRole[] = [UserRole.ADMIN, UserRole.MODERATOR, UserRole.MEMBER];
  if (
    (usersData.data ?? []).filter((u) => u.organization.id === currentOrganizationId).length === 0
  ) {
    availableRoles.push(UserRole.OWNER);
  }

  const isLoading =
    usersData.rest.postStatus === HttpFetchStatus.Loading ||
    usersData.rest.patchStatus === HttpFetchStatus.Loading;

  return (
    <React.Fragment>
      <Dialog open={open} onClose={closeHandler} maxWidth="sm" fullWidth>
        <form onSubmit={addUserClickHandler}>
          <DialogTitle textAlign="center" variant="h2">
            {user?.id != null
              ? `${firstName} ${lastName}`
              : memberRegistration != null
                ? t('title-register-user')
                : t('title-create-user')}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={4} mt={2}>
              {usersData.rest.postError !== null && isHttpError(usersData.rest.postError) && (
                <CustomAlert severity="error" header={t(usersData.rest.postError.message)} />
              )}
              {usersData.rest.patchError !== null && isHttpError(usersData.rest.patchError) && (
                <CustomAlert severity="error" header={t(usersData.rest.patchError.message)} />
              )}
              {memberRegistration?.alert != null && memberRegistration.alert}
              <TextInput
                value={email}
                onChange={emailChangeHandler}
                validationResult={emailValidationResult}
                label={t('input-label-user-email')}
              />
              <Grid container>
                <Grid item xs={12} md={6}>
                  <TextInput
                    value={firstName}
                    onChange={firstNameChangeHandler}
                    validationResult={firstNameValidationResult}
                    label={t('input-label-user-first-name')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextInput
                    value={lastName}
                    onChange={lastNameChangeHandler}
                    validationResult={lastNameValidationResult}
                    label={t('input-label-user-last-name')}
                  />
                </Grid>
              </Grid>
              <TextInput
                value={phoneNumber}
                onChange={phoneNumberChangeHandler}
                validationResult={phoneNumberValidationResult}
                label={t('input-label-user-phone-number')}
              />

              {(user?.id == null || user.role !== UserRole.OWNER) && isAdmin && (
                <SelectInput
                  label={t('input-label-user-role')}
                  value={role}
                  onChange={roleChangeHandler}
                  validationResult={roleValidationResult}
                >
                  {availableRoles.map((r) => (
                    <MenuItem key={r} value={r}>
                      <Stack direction="row" justifyContent="space-between" width="100%">
                        {t('user-role-' + r)}
                        <Tooltip title={t('user-role-description-' + r)} placement="right">
                          <InfoIcon color="disabled" />
                        </Tooltip>
                      </Stack>
                    </MenuItem>
                  ))}
                </SelectInput>
              )}
              <SelectInput
                label={t('input-label-file-language')}
                value={language}
                onChange={languageChangeHandler}
                validationResult={languageValidationResult}
              >
                {Object.values(Language).map((c) => (
                  <MenuItem key={c} value={c}>
                    {t('language-' + c)}
                  </MenuItem>
                ))}
              </SelectInput>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Stack direction="row" spacing={1}>
              <Button onClick={closeHandler}>{t('button-cancel')}</Button>
              <Button
                variant="contained"
                disabled={isLoading || memberRegistration?.formDisabled}
                type="submit"
                data-testid="button-add-user"
              >
                {isLoading ? (
                  <CircularProgress size={24} />
                ) : user?.id == null ? (
                  memberRegistration ? (
                    t('button-register')
                  ) : (
                    t('button-add-user')
                  )
                ) : (
                  t('button-save')
                )}
              </Button>
            </Stack>
          </DialogActions>
        </form>
      </Dialog>
    </React.Fragment>
  );
};

export default UserForm;
